import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  InputAdornment,
  Link,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { useApiGet } from '../../utils/httpClientTs';
import {
  getElfPayPalActivity,
  getElfPayPalTransactions,
} from '../../endpoints/financeElfPayments';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../Components/ServerSideTable';
import type {
  PayPalActivityRow,
  PayPalTransactionRow,
} from '../../types/financePayments';
import { exportToExcel, type ExportColumn } from '../../utils/exportUtils';

const EXPORT_PAGE_SIZE = 100;

const fmtMoney = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const formatIssues = (hasError: boolean) => (hasError ? 'Error' : '');
const formatSchedule = (isRecurring: boolean) =>
  isRecurring ? 'Recurring' : 'One-time';
const formatTime = (value?: string) =>
  value ? new Date(value).toLocaleString() : '';

const settlementResponse = (row: PayPalTransactionRow) =>
  row.response_msg || row.result_code_msg || row.fund_status || '';

const activityResponse = (row: PayPalActivityRow) =>
  row.response_msg ||
  row.result_code_msg ||
  (row.result_code ? `Code ${row.result_code}` : '');

const EXPORT_COLUMNS: ExportColumn[] = [
  { field: 'member', headerName: 'Member' },
  { field: 'time', headerName: 'Time' },
  { field: 'issues', headerName: 'Issues' },
  { field: 'amount', headerName: 'Amount' },
  { field: 'response', headerName: 'Response' },
  { field: 'schedule', headerName: 'Schedule' },
  { field: 'transaction', headerName: 'Transaction' },
];

type Feed = 'settlements' | 'activity';
type RecurringFilter = 'all' | 'recurring' | 'one_time';

const ElfPaymentsPayPalPage: React.FC = () => {
  const [feed, setFeed] = useState<Feed>('settlements');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [unmatchedOnly, setUnmatchedOnly] = useState(false);
  const [hasErrorOnly, setHasErrorOnly] = useState(false);
  const [recurringFilter, setRecurringFilter] =
    useState<RecurringFilter>('all');
  const [delayMember, setDelayMember] = useState('');
  const [member, setMember] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setMember(delayMember.trim());
      setPage(0);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delayMember]);

  const recurringParam =
    recurringFilter === 'recurring'
      ? true
      : recurringFilter === 'one_time'
        ? false
        : null;

  const settlementsQuery = useApiGet(
    [
      'finance-elf-payments-paypal-settlements',
      page,
      rowsPerPage,
      unmatchedOnly,
      hasErrorOnly,
      recurringFilter,
      member,
    ],
    () =>
      getElfPayPalTransactions({
        page: page + 1,
        page_size: rowsPerPage,
        unmatched: unmatchedOnly,
        has_error: hasErrorOnly,
        recurring: recurringParam,
        member: member || undefined,
      }),
    { enabled: feed === 'settlements', retry: 1 }
  );

  const activityQuery = useApiGet(
    [
      'finance-elf-payments-paypal-activity',
      page,
      rowsPerPage,
      hasErrorOnly,
      recurringFilter,
      member,
    ],
    () =>
      getElfPayPalActivity({
        page: page + 1,
        page_size: rowsPerPage,
        has_error: hasErrorOnly,
        recurring: recurringParam,
        member: member || undefined,
      }),
    { enabled: feed === 'activity', retry: 1 }
  );

  const isLoading =
    feed === 'settlements'
      ? settlementsQuery.isLoading
      : activityQuery.isLoading;
  const pagination =
    feed === 'settlements'
      ? settlementsQuery.data?.pagination
      : activityQuery.data?.pagination;
  const totalCount = pagination?.total_count ?? 0;

  const settlementColumns: ServerSideTableColumn<PayPalTransactionRow>[] =
    useMemo(
      () => [
        {
          id: 'payer_label',
          label: 'Member',
          width: '16%',
          firstCell: true,
          sortable: false,
          render: (_v, row) => {
            const label = row.payer_label || '—';
            if (!row.matched_user_id) return label;
            return (
              <Link
                component={RouterLink}
                to={`/finance/elf-loans/${row.matched_user_id}`}
              >
                {label}
              </Link>
            );
          },
        },
        {
          id: 'transaction_time',
          label: 'Time',
          width: '14%',
          sortable: false,
          render: (_v, row) =>
            row.transaction_time
              ? new Date(row.transaction_time).toLocaleString()
              : '—',
        },
        {
          id: 'has_error',
          label: 'Issues',
          width: '8%',
          sortable: false,
          render: (_v, row) =>
            row.has_error ? (
              <Chip size="small" label="Error" color="error" />
            ) : (
              '—'
            ),
        },
        {
          id: 'amount',
          label: 'Amount',
          width: '10%',
          sortable: false,
          render: (_v, row) =>
            `${fmtMoney(row.amount)} ${row.currency_code || ''}`.trim(),
        },
        {
          id: 'response_msg',
          label: 'Response',
          width: '18%',
          sortable: false,
          render: (_v, row) => (
            <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
              {settlementResponse(row) || '—'}
            </Typography>
          ),
        },
        {
          id: 'is_recurring',
          label: 'Schedule',
          width: '10%',
          sortable: false,
          render: (_v, row) => formatSchedule(row.is_recurring),
        },
        {
          id: 'paypal_transaction_id',
          label: 'Transaction',
          width: '14%',
          sortable: false,
          render: (_v, row) => (
            <Link
              component={RouterLink}
              to={`/finance/elf-payments/paypal/${row.id}`}
              variant="body2"
              sx={{ wordBreak: 'break-all' }}
            >
              {row.paypal_transaction_id}
            </Link>
          ),
        },
      ],
      []
    );

  const activityColumns: ServerSideTableColumn<PayPalActivityRow>[] = useMemo(
    () => [
      {
        id: 'payer_label',
        label: 'Member',
        width: '16%',
        firstCell: true,
        sortable: false,
        render: (_v, row) => {
          const label = row.payer_label || '—';
          if (!row.matched_user_id) return label;
          return (
            <Link
              component={RouterLink}
              to={`/finance/elf-loans/${row.matched_user_id}`}
            >
              {label}
            </Link>
          );
        },
      },
      {
        id: 'transaction_time',
        label: 'Time',
        width: '14%',
        sortable: false,
        render: (_v, row) =>
          row.transaction_time
            ? new Date(row.transaction_time).toLocaleString()
            : '—',
      },
      {
        id: 'has_error',
        label: 'Issues',
        width: '8%',
        sortable: false,
        render: (_v, row) =>
          row.has_error ? (
            <Chip size="small" label="Error" color="error" />
          ) : (
            '—'
          ),
      },
      {
        id: 'amount',
        label: 'Amount',
        width: '10%',
        sortable: false,
        render: (_v, row) =>
          `${fmtMoney(row.amount)} ${row.currency_code || ''}`.trim(),
      },
      {
        id: 'response_msg',
        label: 'Response',
        width: '18%',
        sortable: false,
        render: (_v, row) => (
          <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
            {activityResponse(row) || '—'}
          </Typography>
        ),
      },
      {
        id: 'is_recurring',
        label: 'Schedule',
        width: '10%',
        sortable: false,
        render: (_v, row) => formatSchedule(row.is_recurring),
      },
      {
        id: 'paypal_transaction_id',
        label: 'Transaction',
        width: '14%',
        sortable: false,
        render: (_v, row) => (
          <Link
            component={RouterLink}
            to={`/finance/elf-payments/paypal/activity/${row.id}`}
            variant="body2"
            sx={{ wordBreak: 'break-all' }}
          >
            {row.paypal_transaction_id}
          </Link>
        ),
      },
    ],
    []
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      const exportRows: Record<string, string | number>[] = [];
      let pageNum = 1;
      let hasNext = true;

      while (hasNext) {
        if (feed === 'settlements') {
          const resp = await getElfPayPalTransactions({
            page: pageNum,
            page_size: EXPORT_PAGE_SIZE,
            unmatched: unmatchedOnly,
            has_error: hasErrorOnly,
            recurring: recurringParam,
            member: member || undefined,
          });
          for (const row of resp.data) {
            exportRows.push({
              member: row.payer_label || '',
              time: formatTime(row.transaction_time),
              issues: formatIssues(row.has_error),
              amount:
                `${fmtMoney(row.amount)} ${row.currency_code || ''}`.trim(),
              response: settlementResponse(row),
              schedule: formatSchedule(row.is_recurring),
              transaction: row.paypal_transaction_id,
            });
          }
          hasNext = resp.pagination?.has_next ?? false;
        } else {
          const resp = await getElfPayPalActivity({
            page: pageNum,
            page_size: EXPORT_PAGE_SIZE,
            has_error: hasErrorOnly,
            recurring: recurringParam,
            member: member || undefined,
          });
          for (const row of resp.data) {
            exportRows.push({
              member: row.payer_label || '',
              time: formatTime(row.transaction_time),
              issues: formatIssues(row.has_error),
              amount:
                `${fmtMoney(row.amount)} ${row.currency_code || ''}`.trim(),
              response: activityResponse(row),
              schedule: formatSchedule(row.is_recurring),
              transaction: row.paypal_transaction_id,
            });
          }
          hasNext = resp.pagination?.has_next ?? false;
        }
        pageNum += 1;
        if (pageNum > 500) break;
      }

      if (exportRows.length === 0) return;

      exportToExcel(exportRows, EXPORT_COLUMNS, {
        filename: `elf-paypal-${feed}-${new Date().toISOString().slice(0, 10)}`,
        sheetName: feed === 'settlements' ? 'Settlements' : 'Activity',
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {feed === 'settlements'
          ? 'Settlements: match review and settled payment rows. Use Activity for declines / insufficient funds.'
          : 'Activity: daily Payflow attempts including declines and NSF. Not the same as settlement deposits.'}
      </Typography>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        sx={{ mb: 2 }}
        flexWrap="wrap"
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={feed}
          onChange={(_, v: Feed | null) => {
            if (!v) return;
            setFeed(v);
            setPage(0);
            if (v === 'activity') setUnmatchedOnly(false);
          }}
        >
          <ToggleButton value="settlements">Settlements</ToggleButton>
          <ToggleButton value="activity">Activity</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          size="small"
          label="Member"
          placeholder="Name or iMIS"
          value={delayMember}
          onChange={(e) => setDelayMember(e.target.value)}
          sx={{ minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {feed === 'settlements' && (
          <FormControlLabel
            control={
              <Switch
                checked={unmatchedOnly}
                onChange={(e) => {
                  setUnmatchedOnly(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Unmatched only"
          />
        )}
        <FormControlLabel
          control={
            <Switch
              checked={hasErrorOnly}
              onChange={(e) => {
                setHasErrorOnly(e.target.checked);
                setPage(0);
              }}
            />
          }
          label="Errors only"
        />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={recurringFilter}
          onChange={(_, v: RecurringFilter | null) => {
            if (!v) return;
            setRecurringFilter(v);
            setPage(0);
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="recurring">Recurring</ToggleButton>
          <ToggleButton value="one_time">One-time</ToggleButton>
        </ToggleButtonGroup>

        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={exporting || totalCount === 0 || isLoading}
        >
          {exporting ? 'Exporting…' : 'Export Excel'}
        </Button>
      </Stack>

      {feed === 'settlements' ? (
        <ServerSideTable<PayPalTransactionRow>
          data={settlementsQuery.data?.data ?? []}
          columns={settlementColumns}
          loading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          totalPages={pagination?.total_pages ?? 0}
          hasNext={pagination?.has_next ?? false}
          hasPrev={pagination?.has_prev ?? false}
          order="desc"
          orderBy="transaction_time"
          onPageChange={setPage}
          onRowsPerPageChange={(n) => {
            setRowsPerPage(n);
            setPage(0);
          }}
          onSort={() => {}}
          emptyMessage="No PayPal settlement transactions found."
        />
      ) : (
        <ServerSideTable<PayPalActivityRow>
          data={activityQuery.data?.data ?? []}
          columns={activityColumns}
          loading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          totalPages={pagination?.total_pages ?? 0}
          hasNext={pagination?.has_next ?? false}
          hasPrev={pagination?.has_prev ?? false}
          order="desc"
          orderBy="transaction_time"
          onPageChange={setPage}
          onRowsPerPageChange={(n) => {
            setRowsPerPage(n);
            setPage(0);
          }}
          onSort={() => {}}
          emptyMessage="No PayPal activity transactions found."
        />
      )}
    </Box>
  );
};

export default ElfPaymentsPayPalPage;
